import type { AdjustPlatform } from "./platform";

/**
 * プラットフォーム別 Adjust イベントトークン。
 * 定義されていないイベント・プラットフォームの組み合わせは送信しない（ホワイトリスト方式）。
 *
 * CarinaIos 列は 2026-08 のイベント刷新後のトークン。FE イベント名と Adjust
 * ダッシュボード上の登録名が異なる場合は各行のコメントに登録名を記す。
 */
export const ADJUST_TOKENS: Record<
  string,
  Partial<Record<AdjustPlatform, string>>
> = {
  sign_up: {
    RenkaAndroid: "x3qjkl",
    HokageIos: "ydkl5l",
    CarinaIos: "b2cglx", // register
    SakuraIos: "orzovn",
  },
  edit_profile: {
    RenkaAndroid: "74pbz6",
    HokageIos: "fiverv",
    CarinaIos: "5qboys",
    SakuraIos: "dwituj",
  },
  COMPLETE_SEND_MESSAGE: {
    RenkaAndroid: "su7ub9",
    HokageIos: "k49iu2",
    CarinaIos: "fhxi7p", // send_message
    SakuraIos: "bnpxzd",
  },
  COMPLETE_SEND_ONE_TAP_MESSAGE_FROM_BOARD: {
    RenkaAndroid: "jr9d8f",
    HokageIos: "dmcuyy",
    CarinaIos: "ur53h3", // board_onetap
    SakuraIos: "5poajo",
  },
  COMPLETE_SEND_FREE_MESSAGE_FROM_BOARD: {
    RenkaAndroid: "lqe0e4",
    HokageIos: "t0zq52",
    CarinaIos: "1rvkqc", // board_message
    SakuraIos: "ugfsby",
  },
  SEND_GOOD: {
    RenkaAndroid: "bxgz31",
    HokageIos: "97zzxy",
    CarinaIos: "3vfic4", // add_like
    SakuraIos: "71yu13",
  },
  // 接続成立（課金開始0秒）時点。発信は OUTGOING_* 側で videocall_start / voicecall_start を送る
  START_VIDEO_CALL: {
    RenkaAndroid: "7fapxn",
    HokageIos: "p3s6hl",
    CarinaIos: "9u3v8y", // videocall_connect
    SakuraIos: "hni7zu",
  },
  START_VIDEO_CHAT: {
    RenkaAndroid: "9vnh8e",
    HokageIos: "79sddh",
    CarinaIos: "njrz9w", // videochat_start
    SakuraIos: "8mav3l",
  },
  START_VOICE_CALL: {
    RenkaAndroid: "3dglom",
    HokageIos: "xj43uw",
    CarinaIos: "9x72eo", // voicecall_connect
    SakuraIos: "2cric4",
  },
  COMPLETE_BUY_VIDEO: {
    RenkaAndroid: "50iu1f",
    HokageIos: "yn9fqs",
    CarinaIos: "p322bi", // view_video
    SakuraIos: "x3kgyb",
  },
  COMPLETE_BUY_IMAGE: {
    RenkaAndroid: "789ltc",
    HokageIos: "toq8eh",
    CarinaIos: "5ghpzr", // view_image
    SakuraIos: "uyonmu",
  },
  COMPLETE_SEND_GIFT: {
    RenkaAndroid: "5c3bs0",
    HokageIos: "mcs1po",
    CarinaIos: "t7nwjg", // send_gift
    SakuraIos: "gzxzqj",
  },
  COMPLETE_SEND_STAMP: {
    RenkaAndroid: "22cxw5",
    HokageIos: "idzhwu",
    CarinaIos: "xwf0ex", // send_sticker
    SakuraIos: "yyw9fi",
  },
  use_signup_point: {
    RenkaAndroid: "uorvou",
    HokageIos: "li2i9u",
    CarinaIos: "hgzjw1",
    SakuraIos: "85zwfy",
  },
  // Sakura はメニュー種別を区別しないため通常送信・完全コントロールとも同一トークン
  SEND_LOVENSE_IN_VIDEO_CALL: {
    SakuraIos: "xznuk4",
    CarinaIos: "my0vif", // send_video_lovense
  },
  SEND_LOVENSE_IN_VIDEO_CHAT: {
    SakuraIos: "rcjilf",
    CarinaIos: "vth9bd", // send_videochat_lovense
  },
  send_video_complete_lovense: {
    SakuraIos: "xznuk4",
    CarinaIos: "y1c7qw",
  },
  send_videochat_complete_lovense: {
    SakuraIos: "rcjilf",
    CarinaIos: "m0ggsp",
  },
  // --- 以下は Carina のみ送信 ---
  // 通話・ライブ
  OUTGOING_VIDEO_CALL: { CarinaIos: "cj37bq" }, // videocall_start
  OUTGOING_VOICE_CALL: { CarinaIos: "kuim7b" }, // voicecall_start
  INCOMING_VIDEO_CALL: { CarinaIos: "mjanxj" }, // videocall_incoming
  INCOMING_VOICE_CALL: { CarinaIos: "p4vju2" }, // voicecall_incoming
  videocall_end: { CarinaIos: "auvfyx" },
  videochat_end: { CarinaIos: "diqd4j" },
  SEND_MESSAGE_IN_VIDEO_CHAT: { CarinaIos: "acy9j8" }, // videochat_send_message
  // リクエスト
  SEND_VIDEO_CALL_REQUEST: { CarinaIos: "pdfhcb" }, // request_videocall
  SEND_VOICE_CALL_REQUEST: { CarinaIos: "dku7he" }, // request_voicecall
  SEND_IMAGE_FILE_REQUEST: { CarinaIos: "tw6r0m" }, // request_image
  SEND_VIDEO_FILE_REQUEST: { CarinaIos: "wdd4sv" }, // request_video
  // タップ・プロフィールアクション
  ADD_FAVORITE: { CarinaIos: "slp1cg" }, // add_favorite
  remove_favorite: { CarinaIos: "49x8yq" },
  upload_profile_avatar: { CarinaIos: "5zl26m" }, // add_avatar
  add_about_me: { CarinaIos: "uf8atj" },
  add_age: { CarinaIos: "wh02rh" },
  get_dailybonus: { CarinaIos: "lmorji" },
  post_review: { CarinaIos: "b8nwec" },
  // 検索
  tap_search: { CarinaIos: "3letss" },
  search_name: { CarinaIos: "yync7x" },
  search_body: { CarinaIos: "qveles" },
  search_age: { CarinaIos: "4zyrpd" },
  search_location: { CarinaIos: "vw7nva" },
  search_videocall: { CarinaIos: "kh030f" },
  // フリマ
  open_fleamarket_item: { CarinaIos: "i4neiw" },
  view_fleamarket_item_from_profile: { CarinaIos: "4jj446" },
  open_profile_from_fleamarket_item: { CarinaIos: "7n852a" },
  favorite_fleamarket_item: { CarinaIos: "acv1qt" },
  purchased_fleamarket_item: { CarinaIos: "3bz4tm" },
  // 画面表示
  OPEN_GIRLS_ALL: { CarinaIos: "pip0ir" }, // open_home
  OPEN_SEARCH: { CarinaIos: "lzo88w" }, // open_search
  OPEN_BOARD: { CarinaIos: "bxnbgr" }, // open_board
  OPEN_GALLERY: { CarinaIos: "ui2be9" }, // open_gallery
  OPEN_FOOTPRINT_LIST: { CarinaIos: "s2f8rf" }, // open_footstep
  OPEN_BOOKMARK_LIST: { CarinaIos: "esf7u9" }, // open_favorite
  OPEN_LIKE_LIST: { CarinaIos: "zdf7u5" }, // open_like
  OPEN_PROFILE_DETAIL: { CarinaIos: "488m6n" }, // open_profile
  OPEN_FLEA_MARKET: { CarinaIos: "w09i0a" }, // open_fleamarket
};

export const getAdjustToken = (
  eventName: string,
  platform: AdjustPlatform,
): string | undefined => {
  return ADJUST_TOKENS[eventName]?.[platform];
};
